import { Component, Input, OnInit, OnChanges, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgApexchartsModule, ChartComponent } from 'ng-apexcharts';
import {
  ApexNonAxisChartSeries,
  ApexChart,
  ApexResponsive,
  ApexLegend,
  ApexDataLabels,
  ApexPlotOptions
} from 'ng-apexcharts';

export interface CategoryData {
  name: string;
  value: number;
  color: string;
  percentage?: number;
}

export type DonutChartOptions = {
  series: ApexNonAxisChartSeries;
  chart: ApexChart;
  responsive: ApexResponsive[];
  labels: string[];
  colors: string[];
  legend: ApexLegend;
  dataLabels: ApexDataLabels;
  plotOptions: ApexPlotOptions;
};

@Component({
  selector: 'app-sales-category',
  standalone: true,
  imports: [CommonModule, NgApexchartsModule],
  template: `
    <div class="bg-white rounded-xl border border-gray-200 p-6">
      <h3 class="text-lg font-semibold text-gray-900 font-outfit mb-6">{{ title }}</h3>
      
      <!-- Donut Chart -->
      <div class="flex items-center justify-center mb-6">
        <div class="w-48 h-48">
          <apx-chart
            [series]="chartOptions.series"
            [chart]="chartOptions.chart"
            [labels]="chartOptions.labels"
            [colors]="chartOptions.colors"
            [legend]="chartOptions.legend"
            [dataLabels]="chartOptions.dataLabels"
            [plotOptions]="chartOptions.plotOptions"
            [responsive]="chartOptions.responsive">
          </apx-chart>
        </div>
      </div>
      
      <!-- Category legend -->
      <div class="space-y-3">
        @for (category of categoriesWithPercentages; track category.name) {
          <div class="flex items-center justify-between">
            <div class="flex items-center">
              <div 
                class="w-3 h-3 rounded-full mr-3"
                [style.background-color]="category.color">
              </div>
              <span class="text-sm text-gray-600">{{ category.name }}</span>
            </div>
            <div class="text-right">
              <span class="text-sm font-medium text-gray-900">{{ formatCurrency(category.value) }}</span>
              <div class="text-xs text-gray-500">{{ category.percentage }}%</div>
            </div>
          </div>
        }
      </div>
    </div>
  `,
  styles: ``
})
export class SalesCategoryComponent implements OnInit, OnChanges {
  @ViewChild("chart") chart!: ChartComponent;
  @Input() title: string = 'Sales by Category';
  @Input() categories: CategoryData[] = [];

  public chartOptions: DonutChartOptions = {
    series: [],
    chart: {
      width: 200,
      height: 200,
      type: "donut"
    },
    labels: [],
    colors: [],
    dataLabels: {
      enabled: false
    },
    plotOptions: {
      pie: {
        donut: {
          size: '60%',
          labels: {
            show: true,
            total: {
              show: true,
              showAlways: true,
              label: 'Total',
              fontSize: '14px',
              fontFamily: 'Raleway, sans-serif',
              fontWeight: 500,
              color: '#374151',
              formatter: () => {
                return this.formatCurrency(this.totalValue);
              }
            },
            value: {
              show: false
            }
          }
        }
      }
    },
    legend: {
      show: false
    },
    responsive: [
      {
        breakpoint: 480,
        options: {
          chart: {
            width: 180,
            height: 180
          }
        }
      }
    ]
  };

  get totalValue(): number {
    return this.categories.reduce((sum, category) => sum + category.value, 0);
  }

  get categoriesWithPercentages(): (CategoryData & { percentage: number })[] {
    return this.categories.map(category => ({
      ...category,
      percentage: Math.round((category.value / this.totalValue) * 100)
    }));
  }

  ngOnInit() {
    this.updateChartData();
  }

  ngOnChanges() {
    this.updateChartData();
  }

  private updateChartData(): void {
    if (this.categories.length === 0) return;

    this.chartOptions = {
      ...this.chartOptions,
      series: this.categories.map(c => c.value),
      labels: this.categories.map(c => c.name),
      colors: this.categories.map(c => c.color)
    };
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  }
}